/* ═══════════════════════════════════════════════════════
   369Sonic Configurator — 3D Engine + State Machine
   
   Data bridge: reads URLs from window.__CONFIGURATOR__
   (injected by Liquid in the section template)
   ═══════════════════════════════════════════════════════ */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

// ─── Data bridge (injected by Liquid) ───
const CONFIG = window.__CONFIGURATOR__ || {};

// ─── ASA Prusament Color Palette ───
const ASA_COLORS = {
  jet_black:    { hex: '#1C1C1C', metalness: 0.05, roughness: 0.55, label: 'Jet Black' },
  galaxy_black: { hex: '#151515', metalness: 0.15, roughness: 0.45, label: 'Galaxy Black' },
  signal_white: { hex: '#EEEEEE', metalness: 0.02, roughness: 0.60, label: 'Signal White' },
  olive_green:  { hex: '#4A5D23', metalness: 0.02, roughness: 0.60, label: 'Olive Green' },
  prusa_red:    { hex: '#C51111', metalness: 0.02, roughness: 0.60, label: 'Red' },
};

// Default color — no customization surcharge when all colors match this
const DEFAULT_COLOR = 'jet_black';
const DEFAULT_POWER = '70w';
const BUTTON_HEX = '#111111';
const KNIFE_70W_ONLY_BLADES = new Set(['cake', 'bread']);

// ─── Logo Color Map (per-handle-color) ───
const LOGO_COLOR_MAP = {
  jet_black:    '#3A3A3A', // dark gray on black handle
  galaxy_black: '#111111', // black
  signal_white: '#111111', // black
  olive_green:  '#111111', // black
  prusa_red:    '#111111', // black
};

// ═══════════════════════════════════════════════════════
// STATE MACHINE
// ═══════════════════════════════════════════════════════

const state = {
  type: 'knife',
  power: DEFAULT_POWER,
  blade: 'universal',
  charging: 'adapter',
  colors: {
    handle: 'jet_black',
    cover: 'jet_black',
    station: 'jet_black',
  },
  engraving: '',
  giftBox: false,
};

let currentStep = 1;
let isAddingToCart = false;

function is45wAllowed() {
  return !(state.type === 'knife' && KNIFE_70W_ONLY_BLADES.has(state.blade));
}

function syncRadioValue(name, value) {
  document.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
    input.checked = input.value === value;
  });
}

function setPowerWarning(visible) {
  const warningEl = document.getElementById('power-warning');
  if (!warningEl) return;
  warningEl.hidden = !visible;
}

function setBladeWarning(visible) {
  const warningEl = document.getElementById('blade-warning');
  if (!warningEl) return;
  warningEl.hidden = !visible;
}

function getActiveDefaultCoverKey() {
  if (state.type !== 'knife') return null;
  if (state.blade === 'cake') return 'cake';
  if (state.blade === 'bread') return 'bread';
  if (state.blade === 'chef') return 'chef';
  return null;
}

function usesKatanaCover() {
  return state.type === 'cutter' || (state.type === 'knife' && state.blade === 'universal');
}

function isStepDisabled(step) {
  return state.type === 'cutter' && (step === 2 || step === 4);
}

/**
 * Central state updater. Every UI interaction goes through here.
 * @param {string} key - dot-notation path (e.g. 'colors.handle')
 * @param {*} value
 */
function updateState(key, value) {
  if (key === 'power' && value === '45w' && state.type === 'knife' && KNIFE_70W_ONLY_BLADES.has(state.blade)) {
    state.power = '70w';
    syncRadioValue('power', '70w');
    setPowerWarning(true);
    update3DScene();
    if (typeof markActive === 'function') markActive();
    updatePriceUI();
    return;
  }

  // Set value (supports dot-notation for nested keys)
  const parts = key.split('.');
  let target = state;
  for (let i = 0; i < parts.length - 1; i++) {
    target = target[parts[i]];
  }
  target[parts[parts.length - 1]] = value;

  // ── Validation rules ──
  const is45Selected = state.power === '45w';
  document.querySelectorAll('input[name="blade"]').forEach((bladeInput) => {
    const is70wOnlyBlade = KNIFE_70W_ONLY_BLADES.has(bladeInput.value);
    const isBladeDisabled = state.type !== 'knife' || (is45Selected && is70wOnlyBlade);
    bladeInput.disabled = isBladeDisabled;
    bladeInput.setAttribute('aria-disabled', String(isBladeDisabled));
    bladeInput.closest('.wizard-card')?.classList.toggle('wizard-card--disabled', isBladeDisabled);
  });

  if (state.type === 'knife' && is45Selected && KNIFE_70W_ONLY_BLADES.has(state.blade)) {
    state.blade = 'universal';
    syncRadioValue('blade', state.blade);
  }

  setBladeWarning(state.type === 'knife' && is45Selected);
  setPowerWarning(state.type === 'knife' && state.power === '70w' && KNIFE_70W_ONLY_BLADES.has(state.blade));

  // Engraving controls: unavailable for cutter, but the skipped step remains visually normal.
  const isEngravingStepDisabled = state.type === 'cutter';

  const engravingInput = document.getElementById('engraving-input');
  if (engravingInput) {
    if (isEngravingStepDisabled) {
      state.engraving = '';
      engravingInput.value = '';
    }
    engravingInput.disabled = isEngravingStepDisabled;
    engravingInput.setAttribute('aria-disabled', String(isEngravingStepDisabled));
    engravingInput.closest('.wizard-field')?.classList.toggle('wizard-field--disabled', isEngravingStepDisabled);
  }

  if (isEngravingStepDisabled && currentStep === 4) {
    goToStep(5);
  }

  // Station colors: only relevant when wireless
  const stationColorSection = document.getElementById('station-color-section');
  if (stationColorSection) {
    stationColorSection.style.display = state.charging === 'wireless' ? '' : 'none';
  }

  // ── Update 3D scene ──
  update3DScene();

  // ── Wake render loop ──
  if (typeof markActive === 'function') markActive();

  // ── Update price ──
  updatePriceUI();
}

/**
 * Apply all state to the 3D scene
 */
function update3DScene() {
  // Blade type visibility
  const activeBlade = state.type === 'cutter' ? 'cutter' : state.blade;
  Object.entries(bladeMeshes).forEach(([key, mesh]) => {
    if (mesh) mesh.visible = (key === activeBlade);
  });

  // Handle visibility (plastic body)
  if (handleMeshes.knife) handleMeshes.knife.visible = (state.type === 'knife');
  if (handleMeshes.cutter) handleMeshes.cutter.visible = (state.type === 'cutter');

  // Metal accent parts — visible only for knife
  metalMeshes.forEach(m => { m.visible = (state.type === 'knife'); });

  // Buttons — each tagged with _deviceType during catalogue
  buttonMeshes.forEach(m => { m.visible = (state.type === m._deviceType); });

  // Logos — each tagged with _deviceType during catalogue
  logoMeshes.forEach(m => { m.visible = (state.type === m._deviceType); });

  // Lights — each tagged with _deviceType during catalogue (always white glow)
  lightMeshes.forEach(m => { m.visible = (state.type === m._deviceType); });

  // Handle color (ASA Prusament) — applies to plastic handle
  applyASAColor(handleMat, state.colors.handle);

  // Button color — follows handle color for now (future: independent)
  buttonMeshes.forEach(m => {
    if (m.material) {
      m.material.color.set(BUTTON_HEX);
      m.material.needsUpdate = true;
    }
  });

  // Logo color — per-handle mapping (LOGO_COLOR_MAP defined at module scope)
  logoMeshes.forEach(m => {
    if (m.material) {
      const logoHex = LOGO_COLOR_MAP[state.colors.handle] || '#111111';
      m.material.color.set(logoHex);
      m.material.needsUpdate = true;
    }
  });

  // Charging accessories visibility
  // Adapter meshes: visible when adapter is selected
  if (adapterMeshes.length > 0) {
    adapterMeshes.forEach(m => { m.visible = (state.charging === 'adapter'); });
  }
  // Station meshes: visible when wireless is selected
  if (stationMeshes.length > 0) {
    stationMeshes.forEach(m => { m.visible = (state.charging === 'wireless'); });
  }
  if (state.charging === 'wireless' && stationMat) {
    applyASAColor(stationMat, state.colors.station);
  }

  const activeDefaultCoverKey = getActiveDefaultCoverKey();
  if (coverMeshes.universal) coverMeshes.universal.visible = false;
  if (coverMeshes.cake) coverMeshes.cake.visible = activeDefaultCoverKey === 'cake';
  if (coverMeshes.bread) coverMeshes.bread.visible = activeDefaultCoverKey === 'bread';
  if (coverMeshes.chef) coverMeshes.chef.visible = activeDefaultCoverKey === 'chef';
  if (coverMeshes.katana) {
    coverMeshes.katana.visible = usesKatanaCover() && state.type === 'knife';
  }
  if (coverMeshes.cutterKatana) {
    coverMeshes.cutterKatana.visible = usesKatanaCover() && state.type === 'cutter';
  }

  // Katana logo visibility + color — follows handle color when katana is active by default
  if (coverMeshes._katanaLogo) {
    const showKatana = usesKatanaCover() && state.type === 'knife';
    coverMeshes._katanaLogo.visible = showKatana;
    if (showKatana && coverMeshes._katanaLogo.material) {
      const logoHex = LOGO_COLOR_MAP[state.colors.handle] || '#111111';
      coverMeshes._katanaLogo.material.color.set(logoHex);
      coverMeshes._katanaLogo.material.needsUpdate = true;
    }
  }
  if (coverMeshes._cutterKatanaLogo) {
    const showCutterKatana = usesKatanaCover() && state.type === 'cutter';
    coverMeshes._cutterKatanaLogo.visible = showCutterKatana;
    if (showCutterKatana && coverMeshes._cutterKatanaLogo.material) {
      const logoHex = LOGO_COLOR_MAP[state.colors.handle] || '#111111';
      coverMeshes._cutterKatanaLogo.material.color.set(logoHex);
      coverMeshes._cutterKatanaLogo.material.needsUpdate = true;
    }
  }

  // Cover body color — apply to every configurable cover mesh material
  [coverMat, basicCoverMat, cakeCoverMat, breadCoverMat, chefCoverMat, cutterKatanaMat].forEach((mat) => {
    if (mat) applyASAColor(mat, state.colors.cover);
  });

  // Gift box visibility
  if (giftBoxMeshes.length > 0) {
    giftBoxMeshes.forEach(m => { m.visible = state.giftBox === true; });
  }

  // ── Engraving: update texture + visibility ──
  updateEngraving();
}

/**
 * Apply ASA Prusament matte color to a material
 */
function applyASAColor(mat, colorKey) {
  const asa = ASA_COLORS[colorKey];
  if (!mat || !asa) return;

  const apply = (m) => {
    // Dispose GPU textures before nullifying to prevent VRAM leak (fix #9)
    if (m.map) m.map.dispose();
    if (m.roughnessMap) m.roughnessMap.dispose();
    if (m.normalMap) m.normalMap.dispose();
    m.map = null; m.roughnessMap = null; m.normalMap = null;
    m.color.set(asa.hex);
    m.metalness = asa.metalness;
    m.roughness = asa.roughness;
    m.envMapIntensity = 1.2;
    m.needsUpdate = true;
  };

  // Handle multi-material arrays (fix #5: prevents TypeError crash)
  if (Array.isArray(mat)) mat.forEach(apply);
  else apply(mat);
}

// ═══════════════════════════════════════════════════════
// 🚨 NEW DYNAMIC ENGRAVING SYSTEM (Anchor-Based) 🚨
// ═══════════════════════════════════════════════════════
const ENGRAVE_CFG = {
  // 1. Plane Dimensions (Controls the physical size of the text box on the knife)
  planeWidth: 0.06,
  planeHeight: 0.008,

  // 2. Offsets (Pushes the planes OUT from the Empty to sit exactly on the metal)
  offsetX: 0.002,

  // 3. Rotations (Aligns the text to face outward and run along the blade)
  rotFront: { x: 0, y: 90, z: 90 },
  rotBack:  { x: 0, y: -90, z: -90 },

  // 4. Nudges (Slide the text along the blade without opening Blender)
  nudgeY: 0,
  nudgeZ: 0
};

const ENGRAVE_CANVAS_WIDTH = 1024;
const ENGRAVE_CANVAS_HEIGHT = 128;
let _engraveCanvas = null;
let _engraveCtx = null;

function getEngraveCanvas() {
  if (!_engraveCanvas) {
    _engraveCanvas = document.createElement('canvas');
    _engraveCanvas.width = ENGRAVE_CANVAS_WIDTH;
    _engraveCanvas.height = ENGRAVE_CANVAS_HEIGHT;
    _engraveCtx = _engraveCanvas.getContext('2d');
  }
  return { canvas: _engraveCanvas, ctx: _engraveCtx };
}

function renderEngraveTexture(text) {
  const { canvas, ctx } = getEngraveCanvas();
  const w = canvas.width;
  const h = canvas.height;

  ctx.clearRect(0, 0, w, h);
  if (!text || text.trim() === '') return canvas;

  let fontSize = 64;
  ctx.font = `600 ${fontSize}px 'Poppins', 'Helvetica Neue', Arial, sans-serif`;
  let textLength = ctx.measureText(text).width;

  // Auto-shrink font so it never goes off-screen
  if (textLength > w - 40) {
    fontSize = 64 * ((w - 40) / textLength);
    ctx.font = `600 ${fontSize}px 'Poppins', 'Helvetica Neue', Arial, sans-serif`;
    textLength = ctx.measureText(text).width; // re-measure at new size
  }

  // Left-align: text starts at anchor edge, grows away from it
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  ctx.save();
  // Pivot at (20 + textLength, h/2) so that with scale(-1,-1) the text
  // occupies canvas x = 20 … 20+textLength (anchor side, left edge), while:
  //   scale(-1) on X  → cancels the 3D UV horizontal mirror on the blade face
  //   scale(-1) on Y  → cancels Three.js CanvasTexture's natural Y-flip
  // Result: text appears right-side-up and un-mirrored at the correct anchor position.
  ctx.translate(20 + textLength, h / 2);
  ctx.scale(-1, -1);

  // Laser Etch Shadow Pass
  ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = 'rgba(30, 30, 30, 0.9)';
  ctx.fillText(text, 0, 0);

  // Bright Edge Pass
  ctx.shadowColor = 'rgba(255, 255, 255, 0.3)';
  ctx.shadowBlur = 2;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = -1;
  ctx.fillStyle = 'rgba(80, 80, 80, 0.6)';
  ctx.fillText(text, 0, 0);

  ctx.restore();

  // Attach ratio of occupied space (including the 20px padding) so we can mathematically slide the 3D plane
  canvas.__textRatio = Math.min((textLength + 20) / w, 1.0);

  return canvas;
}

function updateEngraving() {
  const activeBlade = state.type === 'cutter' ? 'none' : state.blade;
  const text = state.engraving.trim();

  // Hide all engraving groups first
  Object.values(engraveGroups).forEach(g => g.visible = false);

  const anchor = engraveAnchors[activeBlade];
  if (!text || !anchor) {
    if (typeof markActive === 'function') markActive();
    return;
  }

  // Generate Texture
  const canvas = renderEngraveTexture(text);
  if (!engraveTexture) {
    engraveTexture = new THREE.CanvasTexture(canvas);
    engraveTexture.colorSpace = THREE.SRGBColorSpace;
    if (renderer.capabilities.getMaxAnisotropy) engraveTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
  } else {
    engraveTexture.needsUpdate = true;
  }

  if (customEngraveMat) {
    customEngraveMat.map = engraveTexture;
    customEngraveMat.needsUpdate = true;
  }

  // Dynamically slide the BACK plane so the text (which is rendered left-to-right) 
  // ENDS exactly at the anchor point on the right side.
  const textPhysicalLength = (canvas.__textRatio || 0) * ENGRAVE_CFG.planeWidth;
  const halfW = ENGRAVE_CFG.planeWidth / 2;
  const backCenterY = textPhysicalLength - halfW;

  // Show the active group and update dynamic position
  if (engraveGroups[activeBlade]) {
    const group = engraveGroups[activeBlade];
    if (group.children.length === 2) {
      const backMesh = group.children[1];
      // Overwrite the position.y to slide it down to the exact end of the text
      backMesh.position.y = backCenterY + ENGRAVE_CFG.nudgeY;
    }
    group.visible = true;
  }

  if (typeof markActive === 'function') markActive();
}

// ─── Shopify-native price formatting ───
function formatMoney(cents) {
  const format = CONFIG.moneyFormat || '€{{amount}}';
  const amount = (cents / 100).toFixed(2);
  const amountNoDecimals = Math.round(cents / 100).toString();
  const amountWithComma = amount.replace('.', ',');
  return format
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
    .replace(/\{\{\s*amount\s*\}\}/g, amount.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
}

// ─── Error Toast (replaces blocking alert()) ───
function showToast(message, duration = 4000) {
  const existing = document.getElementById('configurator-toast');
  if (existing) existing.remove();
  const toast = document.createElement('div');
  toast.id = 'configurator-toast';
  toast.className = 'configurator-toast';
  toast.textContent = message;
  document.body.appendChild(toast);
  // Frame delay so the transition fires
  requestAnimationFrame(() => {
    requestAnimationFrame(() => toast.classList.add('configurator-toast--visible'));
  });
  setTimeout(() => {
    toast.classList.remove('configurator-toast--visible');
    toast.addEventListener('transitionend', () => toast.remove(), { once: true });
  }, duration);
}

/**
 * Check whether any color selection differs from the default.
 * Triggers the flat customization surcharge when true.
 */
function hasColorCustomization() {
  if (state.colors.handle !== DEFAULT_COLOR) return true;
  if (state.colors.cover !== DEFAULT_COLOR) return true;
  if (state.charging === 'wireless' && state.colors.station !== DEFAULT_COLOR) return true;
  return false;
}

/**
 * Get the active base tool based on current state.
 * Resolves from CONFIG.baseTools using type-blade-power key.
 * For cutters, blade is forced to 'none'.
 */
function getActiveVariant() {
  if (state.power === '45w' && !is45wAllowed()) return null;
  const blade = state.type === 'cutter' ? 'none' : state.blade;
  const key = `${state.type}-${blade}-${state.power}`;
  return CONFIG.baseTools?.[key] || null;
}

function getCartUnavailableReason() {
  const variant = getActiveVariant();
  if (!variant || !variant.id) return 'This configuration is currently unavailable. Please select a different combination.';
  if (variant.available === false) return 'This configuration is currently sold out.';
  if (state.charging === 'wireless' && (!CONFIG.addons?.chargingStation || CONFIG.addons.chargingStationAvailable === false)) {
    return 'Wireless charging is currently unavailable.';
  }
  if (state.type === 'knife' && state.engraving.trim() && (!CONFIG.addons?.engraving || CONFIG.addons.engravingAvailable === false)) {
    return 'Engraving is currently unavailable.';
  }
  if (state.giftBox && (!CONFIG.addons?.giftBox || CONFIG.addons.giftBoxAvailable === false)) {
    return 'Gift box packaging is currently unavailable.';
  }
  if (hasColorCustomization() && (!CONFIG.addons?.customization || CONFIG.addons.customizationAvailable === false)) {
    return 'Custom colors are currently unavailable.';
  }
  return '';
}

/**
 * Update the price display — multi-product aware
 */
function updatePriceUI() {
  const variant = getActiveVariant();
  const addToCartBtns = document.querySelectorAll('.customize-footer__btn');

  // No product mapped for this combination — show placeholder, disable cart
  if (!variant) {
    const unavailableReason = getCartUnavailableReason();
    document.querySelectorAll('[data-price-display]').forEach(el => {
      el.textContent = '\u2014';
      el.classList.add('customize-footer__price--empty');
    });
    addToCartBtns.forEach(btn => {
      btn.disabled = true;
      btn.title = unavailableReason;
    });
    return;
  }

  let total = variant.price;

  // Add charging station price when wireless
  if (state.charging === 'wireless' && CONFIG.addons?.chargingStationPrice) {
    total += CONFIG.addons.chargingStationPrice;
  }

  // Add engraving surcharge
  if (state.type === 'knife' && state.engraving.trim()) total += (CONFIG.addons?.engravingPrice || 0);

  // Add gift box surcharge
  if (state.giftBox) total += (CONFIG.addons?.giftBoxPrice || 0);

  // Add customization surcharge (flat fee for any non-default color)
  if (hasColorCustomization() && CONFIG.addons?.customizationPrice) {
    total += CONFIG.addons.customizationPrice;
  }

  const formatted = formatMoney(total);
  document.querySelectorAll('[data-price-display]').forEach(el => {
    el.innerHTML = formatted; // innerHTML: Shopify money_format may contain HTML tags
    el.classList.remove('customize-footer__price--empty');
  });

  const unavailableReason = getCartUnavailableReason();
  addToCartBtns.forEach(btn => {
    btn.disabled = isAddingToCart || Boolean(unavailableReason);
    btn.title = unavailableReason;
  });
}

// Upcharge badges removed — price deltas only visible in the bottom total
function renderUpchargeBadges() {}


// ═══════════════════════════════════════════════════════
// THREE.JS SETUP
// ═══════════════════════════════════════════════════════

// Mesh references (populated on model load)
let bladeMat = null;
let handleMat = null;
let stationMat = null;
let coverMat = null;         // Universal katana cover material
let basicCoverMat = null;    // Universal default cover material
let cakeCoverMat = null;     // Cake default cover material
let breadCoverMat = null;    // Bread default cover material
let chefCoverMat = null;     // Chef default cover material
let cutterKatanaMat = null;  // Short cutter katana material
let logoMat = null;       // Logo_Mat — shinier version of handle color
let buttonMat = null;     // Button_Mat — follows handle color (future: independent)
let lightMat = null;      // Light_Mat — always glowing white
const bladeMeshes = {};    // { universal, cake, bread, chef, cutter }
const handleMeshes = {};   // { knife, cutter } — main plastic body
const metalMeshes = [];    // Handle_Metal parts — always metallic
const buttonMeshes = [];   // Handle_Button + Cutter_Button — follow handle color
const logoMeshes = [];     // Handle_Logo + Cutter_Logo — per-handle color mapping
const lightMeshes = [];    // Handle_Light + Cutter_Light — always glowing white
const coverMeshes = {};    // { universal, cake, bread, chef, katana, cutterKatana, _katanaLogo, _cutterKatanaLogo }
const stationMeshes = [];  // wireless charging station sub-meshes
const adapterMeshes = [];  // USB-C adapter sub-meshes
const giftBoxMeshes = [];  // gift box sub-meshes
const engraveAnchors = {};  // { universal, cake, bread, chef } — Empty anchor points
const engraveGroups = {};   // dynamically created plane groups per blade
let customEngraveMat = null; // shared dynamic engrave material
let engraveTexture = null;  // CanvasTexture for dynamic engraving text
let spinGroup = null;
let focusAnim = null;

// ─── Canvas & Renderer ───
const canvas = document.getElementById('configurator-canvas');
if (!canvas) throw new Error('Canvas not found');

const isMobile = () => window.innerWidth < 750;

const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: !isMobile(),
  alpha: true,
  powerPreference: 'high-performance',
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile() ? 1.5 : 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.5;

// ─── Scene ───
const scene = new THREE.Scene();

// ─── Camera ───
const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
camera.position.set(0.6, 0.15, 0);

// "Headlamp" trick: a subtle light moving with the camera
// ensures the metal flat face pointing at the user ALWAYS catches a reflection
const headLamp = new THREE.DirectionalLight(0xffffff, 0.7);
headLamp.position.set(0, 0, 1);
camera.add(headLamp);

// IMPORTANT: camera must be added to the scene for attached lights to work
scene.add(camera);

// ─── OrbitControls ───
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.dampingFactor = 0.08;
controls.enablePan = false;
controls.minDistance = 0.25;
controls.maxDistance = 1.5;
controls.minPolarAngle = Math.PI * 0.15;  // ~27° prevent going under
controls.maxPolarAngle = Math.PI * 0.85;  // ~153° prevent flipping over
controls.target.set(0, 0, 0);
controls.autoRotate = false;
controls.autoRotateSpeed = 0.5;
controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY };
controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: null };

// ─── Lighting ───
// HemisphereLight provides a natural sky-to-ground gradient, lifting absolute blacks
const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.6);
scene.add(hemiLight);

// Key Light: Warm, main studio light from the top right
const keyLight = new THREE.DirectionalLight(0xfff5e6, 2.0);
keyLight.position.set(3, 4, 3);
scene.add(keyLight);

// Fill Light: Cool, fills the shadows softly
const fillLight = new THREE.DirectionalLight(0xe6f0ff, 1.2);
fillLight.position.set(-3, 2, 1);
scene.add(fillLight);

// Rim Light: CRUCIAL for dark backgrounds
// Placed intensely behind the knife to trace a glowing white edge around the dark handles
const rimLight = new THREE.DirectionalLight(0xffffff, 4.0);
rimLight.position.set(0, 3, -5);
scene.add(rimLight);

// Bottom Bounce: Simulates light bouncing up from a table
const bounceLight = new THREE.DirectionalLight(0xffffff, 0.5);
bounceLight.position.set(0, -3, 1);
scene.add(bounceLight);

// ─── HDR Environment Map & Studio Reflections ───
const pmremGenerator = new THREE.PMREMGenerator(renderer);
pmremGenerator.compileEquirectangularShader();

// 1. Instantly apply the premium built-in studio environment
// This acts as a 360° invisible softbox room so metal ALWAYS has something bright to reflect
scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;

// 2. Load custom HDR if provided (will overwrite the RoomEnvironment)
if (CONFIG.hdrUrl) {
  const rgbeLoader = new RGBELoader();
  rgbeLoader.load(
    CONFIG.hdrUrl,
    (hdrTexture) => {
      scene.environment = pmremGenerator.fromEquirectangular(hdrTexture).texture;
      hdrTexture.dispose();
      pmremGenerator.dispose();
    },
    undefined,
    (err) => {
      console.warn('[369s] HDR env map failed to load, keeping RoomEnvironment fallback');
      pmremGenerator.dispose();
    }
  );
} else {
  // No custom HDR — dispose PMREMGenerator to free GPU memory
  pmremGenerator.dispose();
}



// ═══════════════════════════════════════════════════════
// LOAD GLB MODEL
// ═══════════════════════════════════════════════════════

const loader = new GLTFLoader();

// Draco decoder for compressed geometry
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath(CONFIG.dracoDecoderPath || '/assets/');
loader.setDRACOLoader(dracoLoader);

loader.load(
  CONFIG.modelUrl || '',
  (gltf) => {
    const model = gltf.scene;

    // Center & scale — only consider core meshes (exclude accessories that shift the center)
    const box = new THREE.Box3();
    model.traverse((child) => {
      if (!child.isMesh) return;
      const n = child.name || '';
      // Skip accessories/covers that may be positioned far from center
      if (n.includes('Katana_') || n.includes('Cutter_Katana') || n.includes('Accessory_') || n.includes('Cover_') || n.includes('Gift')) return;
      box.expandByObject(child);
    });
    if (box.isEmpty()) box.setFromObject(model); // fallback
    const center = box.getCenter(new THREE.Vector3());
    const sz = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(sz.x, sz.y, sz.z);
    const s = 0.4 / maxDim;
    model.scale.setScalar(s);
    model.position.sub(center.multiplyScalar(s));

    // Force matrix update before any attach() reparenting (fix #1)
    model.updateMatrixWorld(true);

    // ── Catalogue all meshes by name and material (TWO-PASS) ──
    // Pass 1: Identify all meshes by node name
    const knownMeshes = new Set(); // Track identified meshes to exclude from accessories

    model.traverse((child) => {
      if (!child.isMesh) return;
      const nodeName = child.name || '';
      const mats = Array.isArray(child.material) ? child.material : [child.material].filter(Boolean);
      const matName = mats.map(m => m.name || '').join(' ');

      // ── Named blade meshes ──
      if (nodeName.includes('Blade_Cake')) { bladeMeshes.cake = child; knownMeshes.add(child); child.visible = false; }
      if (nodeName.includes('Blade_Chef')) { bladeMeshes.chef = child; knownMeshes.add(child); child.visible = false; }
      if (nodeName.includes('Blade_Bread')) { bladeMeshes.bread = child; knownMeshes.add(child); child.visible = false; }
      if (nodeName.includes('Cutter_Blade')) { bladeMeshes.cutter = child; knownMeshes.add(child); child.visible = false; }

      // ── Knife handle parts ──
      if (nodeName.includes('Handle_Plastic')) {
        handleMeshes.knife = child; knownMeshes.add(child);
        handleMat = child.material;
      }
      if (nodeName.includes('Handle_Metal')) {
        metalMeshes.push(child); knownMeshes.add(child);
        if (!bladeMat) bladeMat = child.material;
      }
      if (nodeName.includes('Handle_Button')) {
        child._deviceType = 'knife';
        buttonMeshes.push(child); knownMeshes.add(child);
        if (!buttonMat) buttonMat = child.material;
      }
      if (nodeName.includes('Handle_Logo')) {
        child._deviceType = 'knife';
        logoMeshes.push(child); knownMeshes.add(child);
        if (!logoMat) logoMat = child.material;
      }
      if (nodeName.includes('Handle_Light')) {
        child._deviceType = 'knife';
        lightMeshes.push(child); knownMeshes.add(child);
        if (!lightMat) lightMat = child.material;
      }

      // ── Cutter handle parts ──
      if (nodeName.includes('Cutter_Handle')) {
        handleMeshes.cutter = child; knownMeshes.add(child); child.visible = false;
        if (!handleMat) handleMat = child.material;
      }
      if (nodeName.includes('Cutter_Button')) {
        child._deviceType = 'cutter';
        buttonMeshes.push(child); knownMeshes.add(child); child.visible = false;
        if (!buttonMat) buttonMat = child.material;
      }
      if (nodeName.includes('Cutter_Logo')) {
        child._deviceType = 'cutter';
        logoMeshes.push(child); knownMeshes.add(child); child.visible = false;
        if (!logoMat) logoMat = child.material;
      }
      if (nodeName.includes('Cutter_Light')) {
        child._deviceType = 'cutter';
        lightMeshes.push(child); knownMeshes.add(child); child.visible = false;
        if (!lightMat) lightMat = child.material;
      }

      // ── Cover / Katana meshes ──
      if (nodeName.includes('Universal_Cover') || nodeName.includes('Cover_Basic')) {
        coverMeshes.universal = child; knownMeshes.add(child); child.visible = false;
        if (!basicCoverMat) basicCoverMat = child.material;
      }
      if (nodeName.includes('Cake_Cover')) {
        coverMeshes.cake = child; knownMeshes.add(child); child.visible = false;
        if (!cakeCoverMat) cakeCoverMat = child.material;
      }
      if (nodeName.includes('Bread_Cover')) {
        coverMeshes.bread = child; knownMeshes.add(child); child.visible = false;
        if (!breadCoverMat) breadCoverMat = child.material;
      }
      if (nodeName.includes('Chef_Cover')) {
        coverMeshes.chef = child; knownMeshes.add(child); child.visible = false;
        if (!chefCoverMat) chefCoverMat = child.material;
      }
      if (nodeName.includes('Katana_Cover')) {
        coverMeshes.katana = child; knownMeshes.add(child); child.visible = false; 
        if (!coverMat) coverMat = child.material;
      }
      if (nodeName.includes('Cutter_Katana_Logo')) {
        coverMeshes._cutterKatanaLogo = child; knownMeshes.add(child); child.visible = false;
      }
      if (nodeName.includes('Cutter_Katana') && !nodeName.includes('Logo')) {
        coverMeshes.cutterKatana = child; knownMeshes.add(child); child.visible = false;
        if (!cutterKatanaMat) cutterKatanaMat = child.material;
      }
      if (nodeName.includes('Katana_Logo') && !nodeName.includes('Cutter_Katana_Logo')) {
        coverMeshes._katanaLogo = child; knownMeshes.add(child); child.visible = false;
        // Uses Logo_Mat — apply same glossy treatment
      }

      // ── Capture blade material from any Blade_Mat mesh ──
      if (matName.includes('Blade') || matName.includes('blade')) {
        if (!bladeMat) bladeMat = child.material;
        knownMeshes.add(child);
      }
      if (matName.includes('Handle') || matName.includes('handle')) {
        if (!handleMat) handleMat = child.material;
        knownMeshes.add(child);
      }
    });

    // Find the universal blade: any Blade_Mat mesh NOT explicitly named
    const namedBladeSet = new Set([bladeMeshes.cake, bladeMeshes.chef, bladeMeshes.bread, bladeMeshes.cutter]);
    model.traverse((child) => {
      if (!child.isMesh || bladeMeshes.universal) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material].filter(Boolean);
      const matName = mats.map(m => m.name || '').join(' ');
      if ((matName.includes('Blade') || matName.includes('blade')) && !namedBladeSet.has(child)
          && !metalMeshes.includes(child) && !buttonMeshes.includes(child)) {
        bladeMeshes.universal = child;
      }
    });

    // Pass 2: Identify accessory meshes, EXCLUDING known meshes
    model.traverse((child) => {
      const nodeName = child.name || '';

      // Wireless charging station
      if (nodeName === 'Accessory_Station' || nodeName === 'Document') {
        child.traverse((sub) => {
          if (sub.isMesh && !knownMeshes.has(sub)) {
            stationMeshes.push(sub);
            sub.visible = false;
            if (!stationMat) stationMat = sub.material;
          }
        });
      }

      // USB-C adapter / cable
      if (nodeName === 'Accessory_Adapter' || nodeName === 'Cable') {
        child.traverse((sub) => {
          if (sub.isMesh && !knownMeshes.has(sub)) {
            adapterMeshes.push(sub);
            // Adapter is default charging, so visible initially
            sub.visible = true;
          }
        });
      }

      // Gift box
      if (nodeName === 'Accessory_GiftBox') {
        child.traverse((sub) => {
          if (sub.isMesh && !knownMeshes.has(sub)) {
            giftBoxMeshes.push(sub);
            sub.visible = false;
          }
        });
      }

      // Engraving anchor (Empty/Plain Axis placed at center of blade)
      if (nodeName === 'Engraving_Anchor') {
        // One shared anchor for all blades
        ['universal', 'cake', 'bread', 'chef'].forEach(k => {
          engraveAnchors[k] = child;
        });
        console.log('[369s] Found Engraving Anchor → shared for all blades');
      }
    });


    // ── Tweak Materials for Realism ──
    if (bladeMat) {
      bladeMat.color.setHex(0xffffff); // Pure bright silver base
      bladeMat.metalness = 0.85;       // < 1.0 prevents the "black void" reflection bug
      bladeMat.roughness = 0.20;       // Sharp reflections, slightly brushed
      bladeMat.envMapIntensity = 2.0;  // Boost HDR reflections aggressively
      bladeMat.needsUpdate = true;
    }

    // Logo material — super glossy for contrast against matte handle
    if (logoMat) {
      logoMat.metalness = 0.15;
      logoMat.roughness = 0.05;          // Near-mirror finish — catches every highlight
      logoMat.envMapIntensity = 2.0;     // Strong environment reflections
      logoMat.needsUpdate = true;
    }

    // Button material — slight sheen
    if (buttonMat) {
      buttonMat.metalness = 0.05;
      buttonMat.roughness = 0.50;
      buttonMat.needsUpdate = true;
    }

    // Light material — always glowing white
    if (lightMat) {
      lightMat.color.setHex(0xffffff);
      lightMat.emissive.setHex(0xffffff);
      lightMat.emissiveIntensity = 3.0;
      lightMat.metalness = 0.0;
      lightMat.roughness = 0.3;
      lightMat.needsUpdate = true;
    }

    // ── Build Dynamic Engraving Planes (attached to anchor Empties) ──
    const planeGeo = new THREE.PlaneGeometry(ENGRAVE_CFG.planeWidth, ENGRAVE_CFG.planeHeight);
    
    customEngraveMat = new THREE.MeshPhysicalMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.FrontSide,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4,
      metalness: 0.1,
      roughness: 0.8,
      color: 0x222222
    });

    const deg2rad = Math.PI / 180;

    Object.entries(engraveAnchors).forEach(([key, anchor]) => {
      const group = new THREE.Group();

      const front = new THREE.Mesh(planeGeo, customEngraveMat);
      const back = new THREE.Mesh(planeGeo, customEngraveMat);

      const halfW = ENGRAVE_CFG.planeWidth / 2;

      // Front shifts +halfW so it goes from Anchor (0) towards Tip
      front.position.set(ENGRAVE_CFG.offsetX, halfW + ENGRAVE_CFG.nudgeY, ENGRAVE_CFG.nudgeZ);
      // Back position is initially set here but dynamically overridden in updateEngraving text-width math!
      back.position.set(-ENGRAVE_CFG.offsetX, halfW + ENGRAVE_CFG.nudgeY, ENGRAVE_CFG.nudgeZ);

      // Rotate to face outward and align with blade length
      front.rotation.set(ENGRAVE_CFG.rotFront.x * deg2rad, ENGRAVE_CFG.rotFront.y * deg2rad, ENGRAVE_CFG.rotFront.z * deg2rad);
      // Force correct unmirrored rotation for the back side
      back.rotation.set(0, -90 * deg2rad, -90 * deg2rad);

      group.add(front);
      group.add(back);
      group.visible = false;

      anchor.add(group);
      engraveGroups[key] = group;
      console.log('[369s] Built engrave planes for:', key);
    });

    // Fix: Ensure cover materials have their own instances (GLB may share
    // a single material across handle + covers — changing one changes all)
    const ensureUniqueCoverMaterial = (meshKey, materialRefName) => {
      let materialRef = ({ coverMat, basicCoverMat, cakeCoverMat, breadCoverMat, chefCoverMat, cutterKatanaMat })[materialRefName];
      const mesh = coverMeshes[meshKey];
      if (!mesh || !materialRef || Array.isArray(materialRef)) return;
      if (materialRef === handleMat || materialRef === coverMat || materialRef === basicCoverMat || materialRef === cakeCoverMat || materialRef === breadCoverMat || materialRef === chefCoverMat || materialRef === cutterKatanaMat) {
        const shouldClone =
          materialRef === handleMat ||
          (materialRefName !== 'coverMat' && materialRef === coverMat) ||
          (materialRefName !== 'basicCoverMat' && materialRef === basicCoverMat) ||
          (materialRefName !== 'cakeCoverMat' && materialRef === cakeCoverMat) ||
          (materialRefName !== 'breadCoverMat' && materialRef === breadCoverMat) ||
          (materialRefName !== 'chefCoverMat' && materialRef === chefCoverMat) ||
          (materialRefName !== 'cutterKatanaMat' && materialRef === cutterKatanaMat);
        if (shouldClone) {
          materialRef = materialRef.clone();
          mesh.material = materialRef;
          if (materialRefName === 'coverMat') coverMat = materialRef;
          if (materialRefName === 'basicCoverMat') basicCoverMat = materialRef;
          if (materialRefName === 'cakeCoverMat') cakeCoverMat = materialRef;
          if (materialRefName === 'breadCoverMat') breadCoverMat = materialRef;
          if (materialRefName === 'chefCoverMat') chefCoverMat = materialRef;
          if (materialRefName === 'cutterKatanaMat') cutterKatanaMat = materialRef;
        }
      }
    };
    ensureUniqueCoverMaterial('katana', 'coverMat');
    ensureUniqueCoverMaterial('universal', 'basicCoverMat');
    ensureUniqueCoverMaterial('cake', 'cakeCoverMat');
    ensureUniqueCoverMaterial('bread', 'breadCoverMat');
    ensureUniqueCoverMaterial('chef', 'chefCoverMat');
    ensureUniqueCoverMaterial('cutterKatana', 'cutterKatanaMat');

    // Upgrade Handle & Station to MeshPhysicalMaterial for premium "satin" plastic sheen
    const upgradeToPhysical = (m) => {
      if (!m || m.isMeshPhysicalMaterial) return m;
      const physicalMat = new THREE.MeshPhysicalMaterial({
        color: m.color,
        map: m.map,
        metalness: m.metalness,
        roughness: m.roughness,
        metalnessMap: m.metalnessMap,
        roughnessMap: m.roughnessMap,
        normalMap: m.normalMap,
        normalScale: m.normalScale,
        aoMap: m.aoMap,
        emissive: m.emissive,
        emissiveMap: m.emissiveMap,
        envMapIntensity: m.envMapIntensity,
        side: m.side,
        clearcoat: 0.15,
        clearcoatRoughness: 0.65,
      });

      // Apply this new material to all meshes currently using the old one
      model.traverse((child) => {
        if (child.isMesh) {
          if (Array.isArray(child.material)) {
            child.material.forEach((mat, i) => { if (mat === m) child.material[i] = physicalMat; });
          } else if (child.material === m) {
            child.material = physicalMat;
          }
        }
      });
      return physicalMat;
    };

    if (handleMat) handleMat = Array.isArray(handleMat) ? handleMat.map(upgradeToPhysical) : upgradeToPhysical(handleMat);
    if (stationMat) stationMat = Array.isArray(stationMat) ? stationMat.map(upgradeToPhysical) : upgradeToPhysical(stationMat);
    if (coverMat) coverMat = Array.isArray(coverMat) ? coverMat.map(upgradeToPhysical) : upgradeToPhysical(coverMat);
    if (basicCoverMat) basicCoverMat = Array.isArray(basicCoverMat) ? basicCoverMat.map(upgradeToPhysical) : upgradeToPhysical(basicCoverMat);
    if (cakeCoverMat) cakeCoverMat = Array.isArray(cakeCoverMat) ? cakeCoverMat.map(upgradeToPhysical) : upgradeToPhysical(cakeCoverMat);
    if (breadCoverMat) breadCoverMat = Array.isArray(breadCoverMat) ? breadCoverMat.map(upgradeToPhysical) : upgradeToPhysical(breadCoverMat);
    if (chefCoverMat) chefCoverMat = Array.isArray(chefCoverMat) ? chefCoverMat.map(upgradeToPhysical) : upgradeToPhysical(chefCoverMat);
    if (cutterKatanaMat) cutterKatanaMat = Array.isArray(cutterKatanaMat) ? cutterKatanaMat.map(upgradeToPhysical) : upgradeToPhysical(cutterKatanaMat);

    console.log('[369s] Mesh catalogue:', {
      blades: Object.keys(bladeMeshes),
      handles: Object.keys(handleMeshes),
      metalParts: metalMeshes.length,
      buttons: buttonMeshes.length,
      logos: logoMeshes.length,
      bladeMat: !!bladeMat,
      handleMat: !!handleMat,
      logoMat: !!logoMat,
    });

    // Apply initial state to scene
    update3DScene();

    // Wrap model in nested groups for rotation
    const flatGroup = new THREE.Group();
    flatGroup.add(model);

    const pivot = new THREE.Group();
    pivot.add(flatGroup);

    spinGroup = new THREE.Group();
    spinGroup.add(pivot);
    scene.add(spinGroup);

    // ── Reparent station/adapter meshes to a static group ──
    // They shouldn't rotate with the knife
    const staticGroup = new THREE.Group();
    scene.add(staticGroup);

    // Use attach() to preserve world transforms when reparenting
    stationMeshes.forEach(mesh => { staticGroup.attach(mesh); });
    adapterMeshes.forEach(mesh => { staticGroup.attach(mesh); });
    giftBoxMeshes.forEach(mesh => { staticGroup.attach(mesh); });

    // Covers + logos — static, shouldn't rotate with the knife
    if (coverMeshes.universal) staticGroup.attach(coverMeshes.universal);
    if (coverMeshes.cake) staticGroup.attach(coverMeshes.cake);
    if (coverMeshes.bread) staticGroup.attach(coverMeshes.bread);
    if (coverMeshes.chef) staticGroup.attach(coverMeshes.chef);
    if (coverMeshes.katana) staticGroup.attach(coverMeshes.katana);
    if (coverMeshes._katanaLogo) staticGroup.attach(coverMeshes._katanaLogo);
    if (coverMeshes.cutterKatana) staticGroup.attach(coverMeshes.cutterKatana);
    if (coverMeshes._cutterKatanaLogo) staticGroup.attach(coverMeshes._cutterKatanaLogo);

    // ── Re-center spinGroup around the knife only ──
    // Compute bounding box from only core meshes (blade + handle), not accessories
    const knifeBox = new THREE.Box3();
    spinGroup.traverse((child) => {
      if (!child.isMesh) return;
      const n = child.name || '';
      if (n.includes('Katana_') || n.includes('Cutter_Katana') || n.includes('Cover_') || n.includes('Accessory_') || n.includes('Gift')) return;
      knifeBox.expandByObject(child);
    });
    if (knifeBox.isEmpty()) knifeBox.setFromObject(spinGroup); // fallback
    const knifeCenter = knifeBox.getCenter(new THREE.Vector3());
    // Offset the model so the knife center is at spinGroup's origin
    flatGroup.position.sub(knifeCenter);
    // Apply the same offset to static accessories so they stay aligned
    staticGroup.position.sub(knifeCenter);

    // ─── Focus presets (cinematic per-step camera choreography) ───
    // Defined as spherical camera positions: theta (horizontal angle), phi (vertical angle), dist (radius)
    const mobTargetY = isMobile() ? -0.03 : 0.00;
    const viewDistanceMultiplier = isMobile() ? 1.22 : 1;
    const FOCUS_PRESETS = {
      // θ=0 = camera at +X, opposite face from the engrave side — shows clean handle front
      overview:  { theta: 0,                           phi: Math.PI / 2.2,      dist: isMobile() ? 1.12 : 1.00, targetY: isMobile() ? -0.07 : -0.03 },
      blade:     { theta: Math.PI / 6,                 phi: Math.PI / 2.5,      dist: isMobile() ? 0.58 : 0.45, targetY: mobTargetY },
      cover:     { theta: Math.PI / 3.4,               phi: Math.PI / 2.45,     dist: isMobile() ? 0.60 : 0.48, targetY: mobTargetY },
      charging:  { theta: -Math.PI / 4,                phi: Math.PI / 2.2,      dist: isMobile() ? 0.82 : 0.70, targetY: mobTargetY },
      // Colors: camera slightly above equator, targetY pulls look-at down to the handle
      // base + charging station area — avoids the "looking up" problem from phi > π/2
      colors:    { theta: Math.PI / 8,                 phi: Math.PI / 2.1,      dist: isMobile() ? 0.88 : 0.72, targetY: -0.10 },
      engrave:   { theta: Math.PI,                     phi: Math.PI / 2.2,      dist: isMobile() ? 0.46 : 0.35, targetY: mobTargetY },
      // Gift box: same angle as overview but zoomed in tighter
      giftbox:   { theta: 0,                           phi: Math.PI / 2.2,      dist: isMobile() ? 0.94 : 0.50, targetY: isMobile() ? -0.06 : -0.03 },
    };
    const DEFAULT_VIEW = isMobile()
      ? { theta: 0, phi: Math.PI / 2.2, dist: 1.12, targetY: -0.07 }
      : { theta: 0, phi: Math.PI / 2.2, dist: 1.00, targetY: -0.03 };

    function presetToPosition(preset) {
      const r = preset.dist * viewDistanceMultiplier;
      const theta = preset.theta;
      const phi = preset.phi;
      return new THREE.Vector3(
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.cos(phi),
        r * Math.sin(phi) * Math.sin(theta)
      );
    }

    function animateToView(preset, duration = 1200) {
      if (focusAnim) cancelAnimationFrame(focusAnim);
      controls.enabled = false;

      const endPos    = presetToPosition(preset);
      const endTarget = new THREE.Vector3(0, preset.targetY || 0, 0);
      const startTarget = controls.target.clone();

      // Convert start + end to spherical so we can arc around the knife
      // instead of cutting straight through its centre (which causes gimbal-lock).
      const startSph = new THREE.Spherical().setFromVector3(camera.position);
      const endSph   = new THREE.Spherical().setFromVector3(endPos);

      // Always take the SHORT way around the horizontal circle (max 180°)
      let dTheta = endSph.theta - startSph.theta;
      if (dTheta >  Math.PI) dTheta -= 2 * Math.PI;
      if (dTheta < -Math.PI) dTheta += 2 * Math.PI;

      const startR   = startSph.radius;
      const startPhi = startSph.phi;
      const startTh  = startSph.theta;
      const startTime = performance.now();

      function tick(now) {
        const t    = Math.min((now - startTime) / duration, 1);
        const ease = 1 - Math.pow(1 - t, 4); // quartic ease-out

        // Interpolate in spherical space — camera stays on sphere surface
        const r     = startR   + (endSph.radius - startR)   * ease;
        const phi   = startPhi + (endSph.phi    - startPhi) * ease;
        const theta = startTh  + dTheta                     * ease;

        camera.position.setFromSpherical(new THREE.Spherical(r, phi, theta));
        controls.target.lerpVectors(startTarget, endTarget, ease);
        controls.update();
        markActive();

        if (t < 1) {
          focusAnim = requestAnimationFrame(tick);
        } else {
          focusAnim = null;
          controls.enabled = true;
        }
      }
      focusAnim = requestAnimationFrame(tick);
    }

    function focusOnPart(category) {
      const preset = FOCUS_PRESETS[category] || FOCUS_PRESETS.overview;
      animateToView(preset);
    }

    function resetFocus() {
      animateToView(DEFAULT_VIEW);
    }

    // Expose globally
    window.focusOnPart = focusOnPart;
    window.resetKnifeFocus = resetFocus;

    // Step focus is now handled by the stepper controller (goToStep)

    // ─── Initial knife presentation (fly-in) ───
    const flyEndPos = presetToPosition(DEFAULT_VIEW);
    const flyStartPos = flyEndPos.clone().multiplyScalar(2.5);
    flyStartPos.y += 0.2;

    controls.enabled = false;
    camera.position.copy(flyStartPos);
    controls.target.set(0, DEFAULT_VIEW.targetY || 0, 0);
    controls.update();
    needsRender = true;

    const flyStart = performance.now();
    const flyDuration = 1500;
    (function flyTick(now) {
      const t = Math.min((now - flyStart) / flyDuration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      camera.position.lerpVectors(flyStartPos, flyEndPos, ease);
      controls.update();
      needsRender = true;
      if (t < 1) {
        focusAnim = requestAnimationFrame(flyTick);
      } else {
        focusAnim = null;
        controls.enabled = true;
      }
    })(performance.now());

    // Hide loading
    setTimeout(() => {
      const el = document.getElementById('configurator-loading');
      if (el) el.classList.add('is-hidden');
    }, 300);
  },
  (progress) => {
    const el = document.getElementById('configurator-loading');
    if (el && progress.total > 0) {
      // progress.loaded can exceed progress.total because Content-Length is the
      // gzip-compressed transfer size while loaded counts decompressed bytes.
      // Clamp to 99 — the loading element is hidden on model-load success.
      const pct = Math.min(Math.round((progress.loaded / progress.total) * 100), 100);
      el.textContent = 'Loading ' + pct + '%';
    }
  },
  (err) => {
    console.error('GLB load error:', err);
    const el = document.getElementById('configurator-loading');
    if (el) el.textContent = 'Failed to load 3D model';
  }
);

// ═══════════════════════════════════════════════════════
// ORBIT CONTROLS & INTERACTION
// ═══════════════════════════════════════════════════════

const container = canvas.parentElement;

// ─── Idle Render System ───
let needsRender = true;
let lastActivityTime = performance.now();
const IDLE_TIMEOUT = 2000; // Stop rendering after 2s of inactivity

function markActive() {
  needsRender = true;
  lastActivityTime = performance.now();
}

// OrbitControls fires 'change' whenever the camera moves
controls.addEventListener('change', markActive);

// ─── WebGL Context Loss Recovery ───
canvas.addEventListener('webglcontextlost', (e) => {
  e.preventDefault();
  const loadEl = document.getElementById('configurator-loading');
  if (loadEl) {
    loadEl.textContent = 'GPU context lost — restoring…';
    loadEl.classList.remove('is-hidden');
  }
});
canvas.addEventListener('webglcontextrestored', () => {
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile() ? 1.5 : 2));
  renderer.setSize(container.clientWidth, container.clientHeight, false);
  markActive();
  const loadEl = document.getElementById('configurator-loading');
  if (loadEl) loadEl.classList.add('is-hidden');
});

// ─── Double-tap / double-click to reset view ───
let lastTapTime = 0;
canvas.addEventListener('pointerdown', (e) => {
  const now = performance.now();
  // Cancel any in-flight focus animation so the user immediately regains control
  if (focusAnim) { cancelAnimationFrame(focusAnim); focusAnim = null; controls.enabled = true; }
  if (now - lastTapTime < 300) {
    // Double-tap: reset to current step's camera preset.
    // Guard: model may not be loaded yet — focusOnPart only exists after GLB load.
    if (window.focusOnPart) {
      const focusTarget = STEP_FOCUS_MAP[currentStep] || 'overview';
      window.focusOnPart(focusTarget);
    }
    lastTapTime = 0;
  } else {
    lastTapTime = now;
  }
});

// ═══════════════════════════════════════════════════════
// RENDER LOOP (idle-aware)
// ═══════════════════════════════════════════════════════

const ro = new ResizeObserver(() => {
  const w = container.clientWidth;
  const h = container.clientHeight;
  if (w === 0 || h === 0) return;
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  markActive();
});
ro.observe(container);
renderer.setSize(container.clientWidth, container.clientHeight, false);
camera.aspect = container.clientWidth / container.clientHeight;
camera.updateProjectionMatrix();

function animate() {
  requestAnimationFrame(animate);

  const timeSinceActivity = performance.now() - lastActivityTime;

  // Auto-rotate after 4s idle
  const shouldAutoRotate = controls.enabled && !focusAnim && timeSinceActivity > 4000;
  if (controls.autoRotate !== shouldAutoRotate) {
    controls.autoRotate = shouldAutoRotate;
  }

  // OrbitControls.update() handles damping + auto-rotate, returns true if view changed
  const changed = controls.update();

  if (changed || needsRender) {
    renderer.render(scene, camera);
    needsRender = false;
    if (changed) lastActivityTime = performance.now();
  }
}
animate();

// ═══════════════════════════════════════════════════════
// UI BINDINGS — Wizard Radios & Inputs
// ═══════════════════════════════════════════════════════

// Generic radio binding: listen to all wizard radios
document.querySelectorAll('.wizard-step input[type="radio"]:not([name$="_color"])').forEach(radio => {
  radio.addEventListener('change', (e) => {
    const key = e.target.name;  // matches state keys: type, power, blade, charging, etc.
    updateState(key, e.target.value);
  });
});

// Color swatch radios (handle, station, cover colors)
document.querySelectorAll('.wizard-swatch input[type="radio"]').forEach(radio => {
  radio.addEventListener('change', (e) => {
    const colorTarget = e.target.name;  // 'handle_color', 'station_color', 'cover_color'
    const stateKey = {
      handle_color: 'colors.handle',
      station_color: 'colors.station',
      cover_color: 'colors.cover',
    }[colorTarget];
    if (stateKey) updateState(stateKey, e.target.value);
  });
});

// Engraving input
const engInput = document.getElementById('engraving-input');
if (engInput) {
  engInput.addEventListener('input', (e) => {
    updateState('engraving', e.target.value);
  });
}

// Gift box checkbox
const giftBoxInput = document.getElementById('gift-box-input');
if (giftBoxInput) {
  giftBoxInput.addEventListener('change', (e) => {
    updateState('giftBox', e.target.checked);
  });
}

// ─── Add to Cart (Multi-Item Payload) ───
document.querySelectorAll('.customize-footer__btn').forEach(btn => {
  btn.addEventListener('click', async () => {
    if (isAddingToCart) return;

    const unavailableReason = getCartUnavailableReason();
    if (unavailableReason) {
      showToast(unavailableReason);
      updatePriceUI();
      return;
    }

    const baseTool = getActiveVariant();

    // Generate unique bundle ID to link all items
    const bundleId = `369_${Date.now().toString(36)}`;
    const items = [];

    // ── Item A: Base Tool ──
    const baseProps = {
      '_bundleId': bundleId,
      'Device Type': state.type.charAt(0).toUpperCase() + state.type.slice(1),
      'Power': state.power.toUpperCase(),
      'Handle Color': ASA_COLORS[state.colors.handle]?.label || state.colors.handle,
    };

    // Blade type (knives only)
    if (state.type === 'knife') {
      baseProps['Blade Type'] = state.blade.charAt(0).toUpperCase() + state.blade.slice(1);
    }

    if (state.colors.cover !== DEFAULT_COLOR || getActiveDefaultCoverKey() || usesKatanaCover()) {
      baseProps['Cover Color'] = ASA_COLORS[state.colors.cover]?.label || state.colors.cover;
    }

    if (usesKatanaCover()) {
      baseProps['Cover'] = 'Katana';
    }

    items.push({
      id: baseTool.id,
      quantity: 1,
      properties: baseProps
    });

    // ── Item B: Charging Station (if wireless) ──
    if (state.charging === 'wireless' && CONFIG.addons?.chargingStation) {
      items.push({
        id: CONFIG.addons.chargingStation,
        quantity: 1,
        properties: {
          '_bundleId': bundleId,
          '_isAddon': 'true',
          'Station Color': ASA_COLORS[state.colors.station]?.label || state.colors.station
        }
      });
    }

    // ── Item C: Engraving Surcharge (+€15) ──
    if (state.type === 'knife' && state.engraving.trim() !== '' && CONFIG.addons?.engraving) {
      items.push({
        id: CONFIG.addons.engraving,
        quantity: 1,
        properties: {
          '_bundleId': bundleId,
          '_isAddon': 'true',
          'Engraving Text': state.engraving.trim().replace(/[<>]/g, '').substring(0, 20)
        }
      });
    }

    // ── Item D: Gift Box Surcharge (+€15) ──
    if (state.giftBox && CONFIG.addons?.giftBox) {
      items.push({
        id: CONFIG.addons.giftBox,
        quantity: 1,
        properties: {
          '_bundleId': bundleId,
          '_isAddon': 'true'
        }
      });
    }

    // ── Item E: Customization Surcharge (color change fee) ──
    if (hasColorCustomization() && CONFIG.addons?.customization) {
      const changedColors = [];
      if (state.colors.handle !== DEFAULT_COLOR) {
        changedColors.push('Handle: ' + (ASA_COLORS[state.colors.handle]?.label || state.colors.handle));
      }
      if (state.colors.cover !== DEFAULT_COLOR) {
        changedColors.push('Cover: ' + (ASA_COLORS[state.colors.cover]?.label || state.colors.cover));
      }
      if (state.charging === 'wireless' && state.colors.station !== DEFAULT_COLOR) {
        changedColors.push('Station: ' + (ASA_COLORS[state.colors.station]?.label || state.colors.station));
      }
      items.push({
        id: CONFIG.addons.customization,
        quantity: 1,
        properties: {
          '_bundleId': bundleId,
          '_isAddon': 'true',
          'Custom Colors': changedColors.join(', ')
        }
      });
    }

    // ── Submit to Cart API ──
    // Fix #11: disable ALL cart buttons to prevent double-submission.
    // Cache each button's original text individually before overwriting.
    isAddingToCart = true;
    const allCartBtns = document.querySelectorAll('.customize-footer__btn');
    const originalText = btn.textContent;
    allCartBtns.forEach(b => {
      b.dataset.originalText = b.textContent;
      b.disabled = true;
      b.textContent = 'ADDING...';
    });
    btn.disabled = true;

    try {
      const cartUrl = CONFIG.cartUrl || '/cart/add.js';
      const response = await fetch(cartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        console.error('Cart API Error:', err);
        throw new Error(err.description || 'Failed to add to cart');
      }

      await response.json();
      btn.textContent = 'ADDED \u2713';
      setTimeout(() => {
        window.location.href = '/cart';
      }, 600);
    } catch (error) {
      console.error('Add to cart error:', error);
      showToast('There was an issue adding this to your cart. Please try again.');
      // Restore each button to its individual original text
      allCartBtns.forEach(b => {
        b.textContent = b.dataset.originalText || originalText;
      });
      isAddingToCart = false;
      updatePriceUI();
    }
  });
});

// ═══════════════════════════════════════════════════════
// STEPPER CONTROLLER
// ═══════════════════════════════════════════════════════


function getTotalSteps() { return 5; }

// Module-level map so the double-tap handler (which runs before GLB load) can
// safely reference it without a ReferenceError.
const STEP_FOCUS_MAP = {
  1: 'overview',   // Device setup → full knife view
  2: 'blade',      // Blade type → 30° rotated, zoomed in
  3: 'colors',     // Colors → 22.5° handle contour
  4: 'engrave',    // Engraving → 90° profile side, close-up
  5: 'giftbox',    // Gift box + cart → angled to show box (right & behind knife)
};

function goToStep(step) {
  const totalSteps = getTotalSteps();
  if (step < 1 || step > totalSteps) return;

  // Auto-skip disabled steps for cutter: blade choice and engraving.
  if (isStepDisabled(step)) {
    const direction = step > currentStep ? 1 : -1;
    let candidate = step + direction;
    while (candidate >= 1 && candidate <= totalSteps && isStepDisabled(candidate)) {
      candidate += direction;
    }
    if (candidate < 1 || candidate > totalSteps) return;
    step = candidate;
  }

  currentStep = step;

  // Auto-expand bottom sheet on mobile when navigating steps
  const sidebar = document.querySelector('.customize-sidebar');
  if (isMobile() && sidebar?.classList.contains('sidebar--collapsed')) {
    sidebar.classList.remove('sidebar--collapsed');
  }

  // Show/hide panels
  document.querySelectorAll('.wizard-step').forEach(el => {
    el.classList.toggle('wizard-step--active', +el.dataset.step === step);
  });

  // Update progress dots & A11y
  document.querySelectorAll('.stepper-dot').forEach(dot => {
    const s = +dot.dataset.step;
    dot.classList.remove('stepper-dot--active', 'stepper-dot--completed');
    dot.removeAttribute('aria-current');
    if (s === step) {
      dot.classList.add('stepper-dot--active');
      dot.setAttribute('aria-current', 'step');
    } else if (s < step) {
      dot.classList.add('stepper-dot--completed');
    }
  });

  // Update filled progress lines (both desktop and mobile)
  const maxDots = getTotalSteps();
  const currentDot = Math.min(step, maxDots);
  const percentage = ((currentDot - 1) / (maxDots - 1)) * 100;
  
  document.querySelectorAll('.js-stepper-fill').forEach(fillLine => {
    fillLine.style.width = `${percentage}%`;
  });

  // Update nav buttons — never hide Back, only hide Next on last step
  const backBtn = document.getElementById('stepper-back');
  const nextBtn = document.getElementById('stepper-next');
  const navEl = document.querySelector('.stepper-nav');
  if (backBtn) backBtn.disabled = (step === 1);
  if (navEl) navEl.style.display = '';
  if (nextBtn) {
    nextBtn.style.display = (step === totalSteps) ? 'none' : '';
    nextBtn.disabled = (step === totalSteps);
  }
  // On mobile last step, show Add to Cart in the nav bar (hides Next button)
  const navCartBtn = document.getElementById('nav-add-to-cart');
  if (navCartBtn) {
    navCartBtn.style.display = (step === totalSteps) ? '' : 'none';
  }

  // Trigger 3D camera focus
  const focusTarget = STEP_FOCUS_MAP[step];
  if (focusTarget && window.focusOnPart) {
    window.focusOnPart(focusTarget);
  }
}

// Dot click handlers
document.querySelectorAll('.stepper-dot').forEach(dot => {
  dot.addEventListener('click', () => {
    const dotStep = +dot.dataset.step;
    goToStep(dotStep);
  });
});

// Back/Next button handlers
const stepperBack = document.getElementById('stepper-back');
const stepperNext = document.getElementById('stepper-next');
if (stepperBack) {
  stepperBack.addEventListener('click', () => goToStep(currentStep - 1));
}
if (stepperNext) {
  stepperNext.addEventListener('click', () => goToStep(currentStep + 1));
}

// Fix #2: mob-add-to-cart already has .customize-footer__btn class,
// so the generic querySelectorAll above binds AJAX directly.
// No proxy click needed (was causing double-fire).

// ─── Initialize on load ───
updatePriceUI();
goToStep(currentStep);

// ─── Mobile Bottom Sheet: Swipe to Collapse/Expand ───
(function initBottomSheetGesture() {
  const sidebar = document.querySelector('.customize-sidebar');
  if (!sidebar) return;

  let touchStartY = 0;
  let touchStartTime = 0;
  let lastTouchToggleTime = 0;
  const SWIPE_THRESHOLD = 40;
  const SHEET_TOGGLE_ZONE = 34;


  function collapse() {
    sidebar.classList.add('sidebar--collapsed');
  }
  function expand() {
    sidebar.classList.remove('sidebar--collapsed');
  }
  function isCollapsed() {
    return sidebar.classList.contains('sidebar--collapsed');
  }
  function toggle() {
    if (isCollapsed()) expand();
    else collapse();
  }
  function isSheetToggleTarget(eventTarget, clientY) {
    if (eventTarget.closest('button, input, label, a')) return false;
    const rect = sidebar.getBoundingClientRect();
    return clientY - rect.top <= SHEET_TOGGLE_ZONE;
  }

  sidebar.addEventListener('touchstart', (e) => {
    if (!isMobile()) return;
    // Fix #14: only allow collapse swipes from header area, not scrollable panels
    if (e.target.closest('.stepper-panels')) {
      touchStartY = null;
      return;
    }
    touchStartY = e.touches[0].clientY;
    touchStartTime = Date.now();
  }, { passive: true });

  sidebar.addEventListener('touchend', (e) => {
    if (!isMobile() || touchStartY === null) return; // Fix #4: null check
    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchEndY - touchStartY;
    const elapsed = Date.now() - touchStartTime;

    // Quick tap on the arrow/header strip toggles the bottom sheet.
    if (Math.abs(deltaY) < 10 && elapsed < 300 && isSheetToggleTarget(e.target, touchEndY)) {
      toggle();
      lastTouchToggleTime = Date.now();
      return;
    }

    // Swipe down → collapse
    if (deltaY > SWIPE_THRESHOLD) {
      collapse();
    }
    // Swipe up → expand
    else if (deltaY < -SWIPE_THRESHOLD) {
      expand();
    }
  }, { passive: true });

  sidebar.addEventListener('click', (e) => {
    if (!isMobile()) return;
    if (Date.now() - lastTouchToggleTime < 500) return;
    if (isSheetToggleTarget(e.target, e.clientY)) {
      toggle();
    }
  });
})();

// Fix #8: Removed destroy() on pagehide — disposing WebGL context
// breaks Safari's BFCache (Back/Forward Cache). The browser handles
// WebGL garbage collection on navigation natively in multi-page apps.
